<template>
  <div class="profile-page">
    <div class="profile-header">患者档案</div>
    <div class="profile-content">
      <aside class="patient-sidebar">
        <div class="patient-search">
          <el-input v-model.trim="quickKeyword" clearable prefix-icon="el-icon-search" placeholder="患者姓名、手机号"
            @input="quickSearch" />
          <!-- <el-button class="filter-icon" icon="el-icon-s-operation" @click="advancedVisible = true" /> -->
        </div>
        <div v-loading="loading" class="patient-items" v-loadMore="loadMorePatients">
          <div v-for="item in patientList" :key="item.patientId" class="patient-item"
            :class="{ active: selectedPatient && selectedPatient.patientId === item.patientId }"
            @click="selectPatient(item)">
            <img v-if="item.sex == '1'" :src="imgArr.girlIcon" class="mr6 patient-sex">
            <img v-else :src="imgArr.manIcon" class="mr6 patient-sex">
            <span class="patient-name">{{ item.patientName }} {{ item.age }}岁</span>
            <span class="patient-spu">尾号: {{ item.phone.slice(-4) }}</span>
          </div>
          <div v-if="!loading && !patientList.length" class="empty-text">暂无患者</div>
        </div>
      </aside>
      <main v-if="selectedPatient" class="profile-main">
        <section class="profile-card patient-summary">
          <div class="summary-top">
            <div class="summary-identity">
              <div class="summary-avatar">
                <i class="el-icon-user-solid"></i>
              </div>
              <div>
                <div class="summary-name-row">
                  <strong class="patient-title">{{ selectedPatient.patientName }}</strong>
                  <span class="patient-sex-text">{{ selectedPatient.sex === '0' ? '男' : '女' }} / {{ selectedPatient.age
                    || '-' }}岁</span>
                </div>
                <div class="tag-list"
                  v-if="(selectedPatient.autoLabels && selectedPatient.autoLabels.length > 0) || (selectedPatient.manualLabels && selectedPatient.manualLabels.length > 0)">
                  <el-tag v-for="tag in selectedPatient.autoLabels" :key="tag" size="mini" effect="plain"
                    :class="tagClassName(tag)">{{ tag }}</el-tag>
                  <el-tag v-for="tag in selectedPatient.manualLabels" :key="tag.labelId" size="mini" effect="plain"
                    :class="tagClassName(tag)">{{ tag.labelName }}</el-tag>
                </div>
              </div>
            </div>
            <el-button type="primary" size="mini" @click="editPatient">编辑信息</el-button>
          </div>
          <div class="overview-grid">
            <section class="overview-card archive-card">
              <div class="section-title"><i />基础档案</div>
              <div class="summary-grid">
                <div><label>手机号码</label>{{ selectedPatient.phone || '-' }}</div>
                <div><label>身份证号</label>{{ maskedIdCard }}</div>
                <div><label>出生日期</label>{{ selectedPatient.birthday || '-' }}</div>
                <div><label>建档日期</label>{{ selectedPatient.createTime || '-' }}</div>
                <div><label>首次就诊</label>{{ selectedPatient.firstVisitDate || '-' }}</div>
                <div>
                  <label>SPU ID</label>
                  {{ selectedPatient.identityId || '-' }}
                  <el-button type="text" @click="querySpu">查看</el-button>
                </div>
              </div>
              <div class="address-container">
                <div class="address-label">家庭住址</div>
                <div class="address-value">{{ selectedPatient.provinceName || '' }}{{ selectedPatient.cityName || '' }}{{ selectedPatient.regionName || '' }}{{ selectedPatient.address || '' }}</div>
              </div>
            </section>
            <section class="overview-card member-section">
              <div class="member-header">
                <div class="section-title"><i />会员关系</div>
                <el-pagination v-if="memberList.length" small layout="total, prev, pager, next"
                  :current-page="memberPage.pageNum" :page-size="memberPage.pageSize" :total="memberList.length"
                  @current-change="handleMemberPageChange" />
              </div>
              <div class="member-list">
                <div v-for="member in pagedMemberList" :key="member.memberId" class="member-card">
                  <div class="member-head">
                    <div class="member-info">
                      <strong>{{ member.memberName || member.name || '-' }}</strong>
                      <span class="member-phone">{{ member.phone || '-' }}</span>
                      <el-tag size="mini" :type="member.status == 0 ? 'success' : 'warning'">
                        {{ member.status == 0 ? '正常' : '黑名单' }}
                      </el-tag>
                    </div>
                  </div>
                  <div class="member-body">
                    <span class="member-body-item">钱包余额 <b>￥{{ member.curAmount || '0.00' }}</b></span>
                    <span class="member-body-item">
                      优惠券
                      <b class="coupon-link" @click="openCouponDialog(member)">{{ member.couponCount || 0 }}张 <i
                          class="el-icon-arrow-right"></i></b>
                    </span>
                  </div>
                  <div class="member-foot">
                    <small>{{ member.bindDate || '-' }}绑定</small>
                    <span @click="toggleBlacklist(member)" class="member-foot-item">
                      <i class="el-icon-user"></i>
                      {{ member.status == 0 ? '加入黑名单' : '移出黑名单' }}
                    </span>
                  </div>
                </div>
                <div v-if="!memberList.length" class="empty-text">暂无会员关系</div>
              </div>
            </section>
          </div>
        </section>

        <section class="stat-grid">
          <div class="stat-card stat-card-blue"><i class="dot " />累计消费<strong>￥{{ formatAmount(summary.price)
          }}</strong></div>
          <div class="stat-card stat-card-orange"><i class="dot " />客单价<strong>￥{{ formatAmount(summary.avgPrice)
          }}</strong>
          </div>
          <div class="stat-card stat-card-green"><i class="dot " />累计就诊<strong>{{ summary.outpatientNumber || 0
          }}次</strong></div>
          <div class="stat-card stat-card-purple"><i class="dot " />近期就诊<strong>{{ summary.recentlyDeptName || '-' }}
              <small>{{
                summary.outpatientValue || '' }}</small></strong></div>
        </section>

        <section class="profile-card visit-section">
          <div class="section-title"><i />就诊记录</div>
          <el-form class="visit-filter" :model="visitFilter" inline size="small">
            <el-form-item label="就诊时间">
              <el-date-picker class="w400" v-model="visitFilter.visitTime" type="datetimerange" value-format="yyyy-MM-dd HH:mm:ss"
                range-separator="至" start-placeholder="开始日期时间" end-placeholder="结束日期时间" :default-time="['00:00:00', '23:59:59']" />
            </el-form-item>
            <el-form-item>
              <el-select v-model="visitFilter.deptId" clearable placeholder="全部医馆">
                <el-option v-for="item in deptOptions" :key="item.deptId" :label="item.deptName" :value="item.deptId" />
              </el-select>
            </el-form-item>
            <el-form-item>
              <el-select v-model="visitFilter.doctorId" clearable filterable remote reserve-keyword placeholder="全部医生"
                :remote-method="searchDoctorOptions">
                <el-option v-for="item in doctorOptions" :key="item.doctorId"
                  :label="item.nickName" :value="item.doctorId" />
              </el-select>
            </el-form-item>
            <el-form-item>
              <el-button type="primary" @click="handleVisitSearch">查询</el-button>
              <el-button @click="handleVisitReset">重置</el-button>
            </el-form-item>
          </el-form>
          <el-table :data="outpatientList" border>
            <el-table-column prop="date" label="就诊时间" min-width="160" />
            <el-table-column prop="deptName" label="医馆" min-width="120" />
            <el-table-column prop="doctorName" label="医生" min-width="100" />
            <el-table-column prop="diagnosis" label="诊断" min-width="180" show-overflow-tooltip />
            <el-table-column label="患者ID" min-width="150">
              <template slot-scope="scope">
                {{ scope.row.patientId }} {{ scope.row.patientName }}
              </template>
            </el-table-column>
            <el-table-column prop="status" label="状态" width="90">
              <template slot-scope="scope">
                <div :class="visitStatusClass(scope.row.status)">{{ computedStatus(scope.row.status).name }}</div>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="90">
              <template slot-scope="scope">
                <el-button type="text" @click="showVisitDetail(scope.row)">查看详情</el-button>
              </template>
            </el-table-column>
          </el-table>
          <div v-if="total > 0" class="table-pagination">
            <el-pagination background small layout="total, prev, pager, next" :current-page="currentPage"
              :page-size="outpatientPageSize" :total="total" @current-change="handleOutpatientCurrentChange" />
          </div>
        </section>

        <div class="handleHistory" v-if="operationRecords.length > 0">
          <div class="color1890 size14 mb20" @click="operationExpanded = !operationExpanded">
            操作记录<i :class="operationExpanded ? 'el-icon-caret-top' : 'el-icon-caret-bottom'"></i>
          </div>
          <div v-if="operationExpanded">
            <div v-for="record in operationRecords" :key="record.createId" class="operation-record-item">
              <div>
                <span class="mr16">{{ record.time }}</span><span class="mr16">{{ record.updateTime }}</span><span
                  class="mr16">{{ record.displayText }}</span>
              </div>
              <div>{{ record.updateBy }}</div>
            </div>
          </div>
        </div>
      </main>
      <div v-else class="profile-empty">请选择患者</div>
    </div>

    <el-dialog title="高级筛选" :visible.sync="advancedVisible" width="520px" custom-class="profile-dialog"
      :close-on-click-modal="false">
      <el-form label-width="90px" class="advanced-form">
        <el-form-item label="患者SPU">
          <el-input v-model.trim="searchForm.identityId" placeholder="请输入" />
        </el-form-item>
        <el-form-item label="患者标签">
          <el-checkbox-group v-model="searchForm.tags">
            <el-checkbox label="老板关系" />
            <el-checkbox label="高消费患者" />
            <el-checkbox label="依赖医保支付" />
          </el-checkbox-group>
        </el-form-item>
        <el-form-item label="建档日期">
          <el-date-picker v-model="searchForm.createTime" type="daterange" value-format="yyyy-MM-dd" range-separator="至"
            start-placeholder="开始时间" end-placeholder="结束时间" />
        </el-form-item>
        <el-form-item label="末次就诊日期">
          <el-date-picker v-model="searchForm.lastVisitTime" type="daterange" value-format="yyyy-MM-dd"
            range-separator="至" start-placeholder="开始时间" end-placeholder="结束时间" />
        </el-form-item>
      </el-form>
      <span slot="footer"><el-button @click="advancedVisible = false">取消</el-button><el-button type="primary"
          @click="applyAdvanced">确定</el-button></span>
    </el-dialog>

    <el-dialog title="编辑信息" :visible.sync="editVisible" width="700px" custom-class="profile-dialog"
      :close-on-click-modal="false">
      <el-form ref="editForm" :model="editForm" :rules="editFormRules" label-width="90px" inline>
        <el-form-item label="姓名" prop="patientName" class="patient-name-item">
          <el-input v-model.trim="editForm.patientName"
            :disabled="Boolean(selectedPatient && (selectedPatient.realIdCard))" />
            <div class="error-tip" v-if="selectedPatient && (selectedPatient.realIdCard)">有医保支付，姓名不可修改</div>
        </el-form-item>
        <el-form-item label="性别" prop="sex">
          <el-radio-group v-model="editForm.sex">
            <el-radio label="0">男</el-radio>
            <el-radio label="1">女</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="出生日期">
          <el-date-picker class="w202" v-model="editForm.birthday" type="date" value-format="yyyy-MM-dd"
            placeholder="选择出生日期" />
        </el-form-item>
        <el-form-item label="身份证号">
          <el-input class="w202" v-if="idCardDisabled" v-model="editForm.idCardText" disabled />
          <el-input class="w202" v-else v-model.trim="editForm.idCard" clearable />
        </el-form-item>
        <el-form-item label="手机号码" prop="phone">
          <el-input v-model.trim="editForm.phone" />
        </el-form-item>
        <el-form-item label="家庭住址">
          <el-cascader class="w202" v-model="editForm.addressRegion" placeholder="请选择省市区" :props="regionProps"
            :options="addressOptions" filterable clearable />
        </el-form-item>
        <el-form-item label="详细地址" prop="address">
          <el-input v-model="editForm.address" placeholder="请输入详细地址"></el-input>
        </el-form-item>
        <el-form-item label="手动标签">
          <el-select v-model="editForm.manualLabelIds" multiple class="w202">
            <el-option v-for="tag in tagOptions" :key="tag.labelId" :label="tag.labelName" :value="tag.labelId" />
          </el-select>
          <span class="error-tip" v-if="editForm.tags && editForm.tags.length > 5">患者标签不能超过5个</span>
        </el-form-item>
        <el-form-item label="自动标签" class="auto-tags-warp">
          <div class="auto-tags" v-if="editForm.autoLabels && editForm.autoLabels.length > 0">
            <div class="auto-tag-item" v-for="(value, index) in editForm.autoLabels" :key="index">{{ value }}</div>
          </div>
        </el-form-item>
      </el-form>
      <span slot="footer"><el-button @click="editVisible = false">取消</el-button><el-button type="primary"
          @click="savePatient">确定</el-button></span>
    </el-dialog>

    <visit-detail-dialog :visible.sync="detailVisible" :order-id="detailOrderId" />

    <el-dialog class="dialogEdit" title="患者SPU" :visible.sync="SPUDialogObj.show" width="860px"
      :close-on-click-modal="false">
      <div>SPU ID:{{ SPUDialogObj.identityId }}</div>
      <el-table class="mt20" :data="SPUDialogObj.patientList" border>
        <el-table-column prop="patientId" label="患者ID" />
        <el-table-column prop="patientName" label="患者姓名" />
        <el-table-column prop="phone" label="患者手机号码" />
        <el-table-column label="操作">
          <template slot-scope="scope">
            <el-button type="text" @click="addSPU(scope.row)">新建SPU</el-button>
            <el-button type="text" @click="openMoveDialog(scope.row)">移动至其他SPU</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-dialog>

    <el-dialog class="dialogEdit" title="转移至其他SPU" :visible.sync="moveDialogObj.show" width="400px"
      :close-on-click-modal="false">
      <div>
        <span>SPU：</span>
        <el-select v-model="moveDialogObj.identityId" filterable remote reserve-keyword placeholder="姓名/手机号/SPU ID"
          :remote-method="remoteMethod">
          <el-option v-for="item in moveDialogObj.options" :key="item.identityId" :value="item.identityId"
            :label="item.name">
            <span>{{ item.identityId }}</span>
            <span class="ml10">{{ item.name }}</span>
            <span class="ml10">{{ item.phone }}</span>
          </el-option>
        </el-select>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="moveDialogCancel">取 消</el-button>
        <el-button type="primary" @click="moveSPU">确定</el-button>
      </span>
    </el-dialog>

    <el-dialog class="coupon-dialog" title="优惠券" :visible.sync="couponDialog.visible" width="1200px"
      :close-on-click-modal="false" @closed="resetCouponDialog">
      <div class="coupon-filter">
        <el-radio-group v-model="couponDialog.status" size="small" @change="handleCouponStatusChange">
          <el-radio-button :label="2">未使用</el-radio-button>
          <el-radio-button :label="1">已使用</el-radio-button>
          <el-radio-button :label="3">已过期</el-radio-button>
        </el-radio-group>
      </div>
      <el-table v-loading="couponDialog.loading" :data="couponDialog.list" border max-height="460">
        <el-table-column prop="couName" label="优惠券名称" min-width="180" show-overflow-tooltip />
        <el-table-column label="备注" min-width="220" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ formatCouponScope(scope.row) }}
          </template>
        </el-table-column>
        <el-table-column v-if="couponDialog.status === 1" prop="useTime" label="使用时间" min-width="160"></el-table-column>
        <el-table-column prop="createDatetime" label="发券时间" min-width="160"></el-table-column>
        <el-table-column label="有效期" min-width="290">
          <template slot-scope="scope">
            {{ formatCouponValidTime(scope.row) }}
          </template>
        </el-table-column>
      </el-table>
      <div v-if="!couponDialog.loading && !couponDialog.list.length" class="coupon-empty">暂无优惠券</div>
    </el-dialog>
  </div>
</template>

<script>
import { patientList, patientInfo, patientEdit, addBlacklist, addIdentity, queryIdentityId, queryIdentity, updateIdentity, getMemberByPatientId, outpatient, patientInfos, getPatientTagList, getPatientOperationLog } from '@/api/patient'
import { deleteMember } from '@/api/member'
import { getMemberCouponList } from '@/api/coupon'
import { deptList } from '@/api/hospital'
import { getDoctorList } from '@/api/operation'
import { provincesList } from "@/api/region"; // 获取区域请求
import { validateName, validatePhone, validateIdCard } from '@/utils/regexp'
import { isNull, debounce } from '@/utils/validateBase'
import { Utils } from "@/utils/utils";
import { imgArr } from '@/assets/images/img'
import VisitDetailDialog from '@/components/VisitDetailDialog.vue'
import { outpatientStatus } from '@/utils/const'

const getEmptyVisitFilter = () => ({
  visitTime: [],
  deptId: '',
  doctorId: ''
})

const getEmptyCouponDialog = () => ({
  visible: false,
  loading: false,
  memberId: '',
  status: 2,
  list: []
})

export default {
  name: 'PatientProfile',
  components: {
    VisitDetailDialog,
  },
  data() {
    return {
      imgArr,
      loading: false,
      quickKeyword: '',
      patientList: [],
      selectedPatient: null,
      memberList: [],
      memberPage: {
        pageNum: 1,
        pageSize: 2
      },
      visitFilter: getEmptyVisitFilter(),
      deptOptions: [],
      doctorOptions: [],
      operationRecords: [],
      operationExpanded: false,
      advancedVisible: false,
      editVisible: false,
      idCardDisabled: false,
      detailVisible: false,
      detailOrderId: '',
      SPUDialogObj: {
        show: false,
        patientList: [],
        identityId: ''
      },
      moveDialogObj: {
        show: false,
        options: [],
        search: '',
        identityId: '',
        patientId: ''
      },
      couponDialog: getEmptyCouponDialog(),
      searchForm: { identityId: '', tags: [], createTime: [], lastVisitTime: [] },
      editForm: {},
      editFormRules: {
        patientName: [{ required: true, validator: validateName, trigger: 'blur' }],
        phone: [{ required: true, validator: validatePhone, trigger: 'blur' }],
        sex: [{ required: true, message: '请选择患者性别', trigger: 'change' }],
        idCard: [{ required: false, validator: validateIdCard, trigger: 'blur' }],
      }, // 表单验证规则
      summary: {},
      tagOptions: [],
      patientTags: [],
      cursorArr: [],//游标数组
      pageSize: 30, // 显示条数
      total: 0, // 总条数
      currentPage: 1, // 当前页
      outpatientPageSize: 10,
      isLoadMore: true,//是否加载更多
      outpatientList: [],//就诊记录
      addressOptions: [],
      regionRequestPromise: null,
      regionProps: { label: "provincesName", value: "provincesId" }, // 区域选择配置

    }
  },
  computed: {
    maskedIdCard() {
      const id = this.selectedPatient && (this.selectedPatient.idCard || this.selectedPatient.realIdCard);
      return id ? this.maskIdCard(id) : '-'
    },
    pagedMemberList() {
      const start = (this.memberPage.pageNum - 1) * this.memberPage.pageSize
      return this.memberList.slice(start, start + this.memberPage.pageSize)
    }
  },
  created() {
    if (window.sessionStorage.getItem('patientListFilter')) {
      const info = JSON.parse(window.sessionStorage.getItem('patientListFilter'));
      this.searchForm = info.form
      this.pageSize = info.pageSize
      window.sessionStorage.removeItem('patientListFilter')
    } else {
      this.cursorArr.push(null)
    }
    this.getPatients()
    this.getDeptOptions()
    this.searchDoctorOptions('')
  },
  methods: {
    maskIdCard(idCard) {
      const id = String(idCard || '');
      if (!id) return '';
      const starCount = Math.max(1, id.length - 10);
      return id.slice(0, 6) + '*'.repeat(starCount) + id.slice(-4);
    },
    computedStatus(status) {
      return outpatientStatus.find(item => item.id == status) || { tag: '', name: '-' }
    },
    formatAmount(value) {
      if (value === 0 || value === '0') return '0.00'
      if (!this.hasValue(value)) return '0.00'
      const number = Number(value)
      return Number.isFinite(number) ? number.toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : value
    },
    hasValue(value) {
      return value !== '' && value !== null && value !== undefined
    },
    tagClassName(tag) {
      if (tag === '老板关系') return 'tag-boss'
      if (tag === '高消费患者') return 'tag-high'
      if (tag === '依赖医保支付') return 'tag-insurance'
      return ''
    },
    visitStatusClass(status) {
      return [8, 2].includes(Number(status)) ? 'infoTag' : 'normalTag'
    },
    getEmptyVisitFilter() {
      return getEmptyVisitFilter()
    },
    handleMemberPageChange(pageNum) {
      this.memberPage.pageNum = pageNum
    },
    async getDeptOptions() {
      try {
        const res = await deptList({ pageNum: 1, pageSize: 1000 })
        this.deptOptions = res && Number(res.code) === 200 && Array.isArray(res.rows) ? res.rows : []
      } catch (error) {
        this.deptOptions = []
      }
    },
    searchDoctorOptions: debounce(async function (query) {
      try {
        const res = await getDoctorList({ searchKey: query })
        this.doctorOptions = res && Number(res.code) === 200 && Array.isArray(res.data) ? res.data : []
      } catch (error) {
        this.doctorOptions = []
      }
    }, 300),
    handleVisitSearch() {
      this.currentPage = 1
      this.getOutpatientList(this.selectedPatient && this.selectedPatient.identityId)
    },
    handleVisitReset() {
      this.visitFilter = getEmptyVisitFilter()
      this.currentPage = 1
      this.getOutpatientList(this.selectedPatient && this.selectedPatient.identityId)
    },
    loadMorePatients: debounce(function () {
      if (Utils.lt(this.patientList.length, this.pageSize)) return
      this.getPatients()
    }, 300),
    async getPatients() {
      this.loading = true;
      let params = { cursorIndex: this.cursorArr[this.cursorArr.length - 1], pageSize: this.pageSize, ...this.searchForm };
      const { data, code } = await patientList(params);
      if (code === 200) {
        this.cursorArr.push(isNull(data.cursorIndex) ? null : data.cursorIndex)
        this.patientList = [...this.patientList, ...(data && data.patients) || []];
        this.isLoadMore = Utils.lt(this.patientList.length, this.pageSize) ? false : true
        if (!this.selectedPatient && this.patientList.length) this.selectPatient(this.patientList[0])
      }
      this.loading = false
    },
    quickSearch: debounce(function () {
      this.isLoadMore = true
      this.cursorArr = []
      this.cursorArr.push(null)
      this.patientList = []
      this.searchForm = { patientName: this.quickKeyword };
      this.getPatients()
    }, 900),
    applyAdvanced() {
      this.advancedVisible = false;
      this.searchForm = {};
      this.getPatients()
    },
    selectPatient(item) {
      this.selectedPatient = item;
      this.operationExpanded = false;
      this.currentPage = 1;
      this.memberPage.pageNum = 1;
      this.visitFilter = getEmptyVisitFilter();
      this.loadPatientData(item)
    },
    async loadPatientData(item) {
      const [infoRes, memberRes] = await Promise.all([
        patientInfo(item.patientId),
        getMemberByPatientId({ patientId: item.patientId }),
      ]);
      if (infoRes.code === 200) {
        this.selectedPatient = { ...item, ...infoRes.data };
        this.patientTags = infoRes.data.tags || infoRes.data.labels || item.tags || [];
      }
      this.memberList = memberRes.code === 200 ? (memberRes.data || []) : [];
      this.memberPage.pageNum = 1;
      await Promise.all([
        this.getOutpatientList(item.identityId),
        this.getOperationRecords(item.patientId)
      ])
      this.getPatientsData()
    },
    async getOutpatientList(identityId) {
      if (!identityId) {
        this.outpatientList = [];
        this.total = 0;
        return;
      }
      const params = { identityId, pageNum: this.currentPage, pageSize: this.outpatientPageSize };
      if (this.visitFilter.visitTime && this.visitFilter.visitTime.length === 2) {
        params.startDate = this.visitFilter.visitTime[0];
        params.endDate = this.visitFilter.visitTime[1];
      }
      if (this.hasValue(this.visitFilter.deptId)) params.deptId = this.visitFilter.deptId;
      if (this.hasValue(this.visitFilter.doctorId)) params.doctorId = this.visitFilter.doctorId;
      params.deptId = params.deptId ? params.deptId : 'all'
      const res = await outpatient(params);
      if (res.code === 200) {
        this.outpatientList = res.rows || (res.data && res.data.rows) || [];
        this.total = res.total || (res.data && res.data.total) || 0;
      } else {
        this.outpatientList = [];
        this.total = 0;
      }
    },
    async getOperationRecords(patientId) {
      if (!patientId) {
        this.operationRecords = [];
        return;
      }
      const res = await getPatientOperationLog({ patientId });
      if (res.code === 200) {
        const list = res.rows || [];
        this.operationRecords = list
      } else {
        this.operationRecords = [];
      }
    },
    handleOutpatientCurrentChange(page) {
      this.currentPage = page;
      this.getOutpatientList(this.selectedPatient && this.selectedPatient.identityId);
    },

    editPatient() {
      this.editForm = { ...this.selectedPatient, addressRegion: [], tags: [...this.patientTags] };
      this.idCardDisabled = Boolean(this.editForm.idCard);
      if (this.idCardDisabled) {
        this.editForm.idCardText = this.maskIdCard(this.editForm.idCard);
      }
      this.getProvincesList(); // 请求区域列表
      this.getPatientTagList(); // 请求患者标签列表
      if (this.editForm.province && this.editForm.city && this.editForm.region) {
        this.editForm.addressRegion = [Number(this.editForm.province), Number(this.editForm.city), Number(this.editForm.region)]
        this.editForm.provinceName = this.editForm.provinceName
        this.editForm.cityName = this.editForm.cityName
        this.editForm.regionName = this.editForm.region
      }
      this.editVisible = true
    },
    savePatient() {
      this.$refs.editForm.validate(valid => {
        if (!valid) return;
        if (this.editForm.addressRegion && this.editForm.addressRegion.length > 0) {
          this.editForm.province = this.editForm.addressRegion[0]
          this.editForm.city = this.editForm.addressRegion[1]
          this.editForm.region = this.editForm.addressRegion[2]
          this.editForm.provinceName = this.getAddressName(this.editForm.addressRegion).provinceName
          this.editForm.cityName = this.getAddressName(this.editForm.addressRegion).cityName
          this.editForm.regionName = this.getAddressName(this.editForm.addressRegion).regionName
        }else{
          this.editForm.provinceName = ''
          this.editForm.cityName = ''
          this.editForm.regionName = ''
          this.editForm.province = ''
          this.editForm.city = ''
          this.editForm.region = ''
        }
        const params = { ...this.editForm };
        delete params.idCardText;
        patientEdit(params).then(res => {
          if (res.code === 200) {
            this.$message.success('修改成功');
            this.editVisible = false;
            this.loadPatientData(this.selectedPatient)
          }
        })
      })
    },
    async toggleBlacklist(member) {
      // 二次确认
      this.$confirm('确定要' + (member.status == 0 ? '加入' : '移出') + '黑名单吗?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        const action = member.status == 0 ? '加入' : '移出';
        const res = member.status == 0 ? await addBlacklist({ memberId: member.memberId }) : await deleteMember(member.memberId);
        if (res.code === 200) {
          this.$message.success(`${action}成功`)
          this.loadPatientData(this.selectedPatient)
        }
      }).catch(() => { });
    },
    showVisitDetail(record) {
      const orderId = record && record.orderId
      if (!orderId) return this.$message.warning('当前门诊记录缺少orderId')
      this.detailOrderId = orderId;
      this.detailVisible = true
    },
    async querySpu() {
      const identityId = this.selectedPatient && this.selectedPatient.identityId
      if (!identityId) return this.$message.warning('暂无SPU ID')
      this.SPUDialogObj.identityId = ''
      const { code, data } = await queryIdentityId({ identityId })
      if (code == 200) {
        this.SPUDialogObj.patientList = data || []
        this.SPUDialogObj.show = true
        this.SPUDialogObj.identityId = identityId
      }
    },
    addSPU(row) {
      this.$confirm('确定要新建SPU吗?', '新建SPU', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        const { code } = await addIdentity({ patientId: row.patientId })
        if (code == 200) {
          this.$message.success('新建成功')
          this.SPUDialogObj.show = false
          this.loadPatientData(this.selectedPatient)
        }
      }).catch(() => { })
    },
    remoteMethod: debounce(async function (val) {
      if (!val) {
        this.moveDialogObj.options = []
        return
      }
      const { code, data } = await queryIdentity({ search: val })
      if (code == 200) {
        this.moveDialogObj.options = data || []
      }
    }, 300),
    openMoveDialog(row) {
      this.moveDialogObj.patientId = row.patientId
      this.moveDialogObj.show = true
      this.moveDialogObj.search = ''
      this.moveDialogObj.options = []
      this.moveDialogObj.identityId = ''
    },
    moveDialogCancel() {
      this.moveDialogObj.patientId = ''
      this.moveDialogObj.show = false
    },
    async moveSPU() {
      if (!this.moveDialogObj.patientId || !this.moveDialogObj.identityId) {
        return this.$message.warning('请选择目标SPU')
      }
      const params = {
        identityId: this.moveDialogObj.identityId,
        patientId: this.moveDialogObj.patientId
      }
      const { code } = await updateIdentity(params)
      if (code == 200) {
        this.$message.success('操作成功')
        this.SPUDialogObj.show = false
        this.moveDialogObj.show = false
        this.loadPatientData(this.selectedPatient)
      }
    },
    openCouponDialog(member) {
      if (!member || !member.memberId) return this.$message.warning('暂无会员信息')
      this.couponDialog = {
        ...getEmptyCouponDialog(),
        visible: true,
        memberId: member.memberId,
        status: 2
      }
      this.loadMemberCoupons()
    },
    resetCouponDialog() {
      this.couponDialog = getEmptyCouponDialog()
    },
    handleCouponStatusChange() {
      this.loadMemberCoupons()
    },
    async loadMemberCoupons() {
      if (!this.couponDialog.memberId) {
        this.couponDialog.list = []
        return
      }
      this.couponDialog.loading = true
      try {
        const { code, data } = await getMemberCouponList({
          memberId: this.couponDialog.memberId,
          status: this.couponDialog.status
        })
        if (code === 200) {
          this.couponDialog.list = Array.isArray(data) ? data : []
        } else {
          this.couponDialog.list = []
        }
      } catch (error) {
        this.couponDialog.list = []
      } finally {
        this.couponDialog.loading = false
      }
    },
    formatCouponScope(row) {
      if (!row) return '-'
      const scopes = []
      if (Array.isArray(row.deptNames) && row.deptNames.length) scopes.push(`医馆：${row.deptNames.join('、')}`)
      if (Array.isArray(row.doctorNames) && row.doctorNames.length) scopes.push(`医生：${row.doctorNames.join('、')}`)
      if (Array.isArray(row.medicineNames) && row.medicineNames.length) scopes.push(`药品：${row.medicineNames.join('、')}`)
      if (row.couNotes) scopes.push(row.couNotes)
      return scopes.length ? scopes.join('；') : '全部可用'
    },
    formatCouponValidTime(row) {
      if (!row) return '-'
      const begin = row.couBeginTime || row.createDatetime || ''
      const end = row.couEndTime || ''
      if (begin && end) return `${begin} ~ ${end}`
      if (end) return end
      if (begin) return begin
      return '-'
    },
    couponStatusText(status) {
      if (Number(status) === 1) return '已使用'
      if (Number(status) === 3) return '已过期'
      return '未使用'
    },
    couponStatusClass(status) {
      if (Number(status) === 1) return 'coupon-status used'
      if (Number(status) === 3) return 'coupon-status expired'
      return 'coupon-status unused'
    },
    async getPatientsData() {
      const { code, data } = await patientInfos(this.selectedPatient.patientId);
      if (code === 200) {
        this.summary = data || {};
      }
    },
    // 请求所有区域列表
    getProvincesList() {
      if (this.addressOptions.length) return Promise.resolve(this.addressOptions)
      if (this.regionRequestPromise) return this.regionRequestPromise

      this.regionRequestPromise = provincesList().then(({ rows, code }) => {
        try {
          if (code == 200) this.listToTree(Array.isArray(rows) ? rows : [])
          return this.addressOptions
        } finally {
          this.regionRequestPromise = null
        }
      }, error => {
        this.regionRequestPromise = null
        return Promise.reject(error)
      })

      return this.regionRequestPromise
    },
    // 链式结构转树形结构
    listToTree(list = []) {
      const nodeMap = new Map()
      const roots = []

      // 先建立索引，避免在每一级中反复遍历整个区域树。
      list.forEach(item => {
        if (!item || isNull(item.provincesId)) return
        nodeMap.set(String(item.provincesId), {
          ...item,
          children: []
        })
      })

      // 按 parentId 一次性挂载子节点，保持接口返回顺序。
      nodeMap.forEach(node => {
        const parent = nodeMap.get(String(node.parentId))
        if (parent && parent !== node) {
          parent.children.push(node)
        } else if (node.type == '0') {
          roots.push(node)
        }
      })

      // 保持原有级联选择器的叶子节点处理逻辑。
      nodeMap.forEach(node => {
        if (node.type == '3' || (node.type == '2' && node.children.length === 0)) {
          node.children = null
        }
      })

      this.addressOptions = roots
    },
    getAddressName(addressRegion) {
      const addressName = {
        provinceName: '',
        cityName: '',
        regionName: ''
      }
      if (isNull(addressRegion)) return addressName
      const regionIds = Array.isArray(addressRegion) ? addressRegion : [addressRegion]
      const nameKeys = ['provinceName', 'cityName', 'regionName']
      let options = this.addressOptions
      regionIds.slice(0, nameKeys.length).forEach((regionId, index) => {
        const region = (options || []).find(item => String(item.provincesId) === String(regionId))
        if (!region) return
        addressName[nameKeys[index]] = region.provincesName
        options = region.children
      })
      return addressName
    },
    async getPatientTagList() {
      const { code, data } = await getPatientTagList();
      if (code === 200) {
        this.tagOptions = data || [];
      }
    },
  },
}
</script>

<style lang="less" scoped>
.profile-page {
  height: 100%;
  background: #f5f6f8;
  color: #303133;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.profile-header {
  height: 56px;
  line-height: 56px;
  padding: 0 24px;
  background: #fff;
  font-weight: bold;
  font-size: 16px;
  color: #000000;
  border-bottom: 1px solid #F4F4F4;
}

.profile-content {
  display: flex;
  flex: 1;
  overflow: hidden;
}


.patient-sidebar {
  width: 300px;
  height: 100%;
  flex-shrink: 0;
  background: #fff;
  border-right: 1px solid #e7e9ed;
  overflow: hidden;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.patient-search {
  display: flex;
  padding: 12px 16px;

  .el-input {
    flex: 1;
  }

  .filter-icon {
    margin-left: 5px;
    padding: 8px;
  }
}

.patient-items {
  border-top: 1px solid #ebeef5;
  flex: 1;
  overflow: auto;
  padding: 12px;
  box-sizing: border-box;
}

.patient-item {
  display: flex;
  align-items: center;
  height: 40px;
  padding: 0 10px;
  cursor: pointer;
  font-size: 14px;
  border-radius: 4px;

  &:hover,
  &.active {
    color: #fff;
    background: #168cf0;
  }
}

.patient-sex {
  width: 20px;
  height: 20px;
}

.patient-name {
  flex: 1;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.patient-spu {
  font-size: 12px;
}

.profile-main {
  flex: 1;
  min-width: 0;
  padding: 16px;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.profile-card {
  margin-bottom: 0;
  padding: 16px 18px;
  background: #fff;
  border: 1px solid #edf0f3;
  border-radius: 8px;
}

.summary-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-bottom: 16px;
}

.summary-identity {
  display: flex;
  align-items: center;
}

.summary-avatar {
  width: 48px;
  height: 48px;
  margin-right: 12px;
  border-radius: 50%;
  background: #e8f4ff;
  color: #1890ff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}

.summary-name-row {
  display: flex;
  align-items: baseline;
}

.patient-title {
  margin-right: 8px;
  font-size: 20px;
  font-weight: bold;
  color: #000000;
}

.patient-sex-text {
  font-weight: 400;
  font-size: 14px;
  color: #667182;
}

.tag-list {
  margin-top: 8px;

  .el-tag {
    margin-right: 8px;
    border-radius: 4px;
  }

  .tag-boss {
    color: #7a5af8;
    background: #f3efff;
    border-color: #e3d9ff;
  }

  .tag-high {
    color: #d48806;
    background: #fff7e6;
    border-color: #ffe7ba;
  }

  .tag-insurance {
    color: #1890ff;
    background: #e6f7ff;
    border-color: #bae7ff;
  }
}

.overview-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.overview-card {
  min-width: 0;
  padding: 16px;
  border: 1px solid #edf0f3;
  border-radius: 8px;
  background: #fff;
  box-sizing: border-box;
}

.summary-grid {
  display: grid;
  // 一行显示2个
  grid-template-columns: repeat(2, 1fr);
  gap: 12px 24px;
  font-weight: 400;
  font-size: 14px;
  color: #000000;

  div {
    display: flex;
    align-items: center;
    min-width: 0;
    flex-wrap: wrap;
  }

  label {
    display: inline-block;
    width: 70px;
    flex-shrink: 0;
    font-weight: 400;
    font-size: 14px;
    color: #667182;
  }

  .el-button--text {
    padding: 0 4px;
    margin-left: 4px;
  }
}

.address-container {
  display: flex;
  margin-top: 12px;
  .address-label {
    display: inline-block;
    width: 70px;
    flex-shrink: 0;
    font-weight: 400;
    font-size: 14px;
    color: #667182;
  }
  .address-value {
    font-weight: 400;
    font-size: 14px;
    color: #000000;
    line-height: 1.5;
  }
}

.stat-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.stat-card {
  padding: 14px;
  background: #fff;
  border-radius: 5px;
  font-weight: 500;
  font-size: 14px;
  color: #667182;
  height: 96px;
  border-top: 3px solid;
  box-sizing: border-box;

  .dot {
    display: inline-block;
    width: 14px;
    height: 14px;
    margin-right: 5px;
    border-radius: 50%;
    position: relative;

    &::after {
      content: '';
      display: block;
      width: 7px;
      height: 7px;
      border-radius: 50%;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 1;
    }
  }

  strong {
    display: block;
    margin-top: 8px;
    font-weight: bold;
    font-size: 24px;
    color: #000000;
  }

  small {
    margin-left: 8px;
    color: #909399;
    font-size: 11px;
  }
}

.stat-card-blue {
  .dot {
    background: rgba(24, 144, 255, 0.15);

    &::after {
      background: #1890FF;
    }
  }

  border-color: #1890FF;

}

.stat-card-orange {
  .dot {
    background: rgba(245, 166, 35, 0.15);

    &::after {
      background: #f5a623;
    }
  }

  border-color: #f5a623;

}

.stat-card-green {
  .dot {
    background: rgba(86, 196, 26, 0.15);

    &::after {
      background: #56c41a;
    }
  }

  border-color: #56c41a;

}

.stat-card-purple {
  .dot {
    background: rgba(160, 91, 229, 0.15);

    &::after {
      background: #a05be5;
    }
  }

  border-color: #a05be5;


}

.section-title {
  margin-bottom: 12px;
  font-weight: bold;
  font-size: 16px;
  color: #000000;

  i {
    display: inline-block;
    width: 4px;
    height: 16px;
    margin-right: 6px;
    vertical-align: -2px;
    background: #168cf0;
    border-radius: 4px;
  }
}

.member-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;

  .section-title {
    margin-bottom: 0;
  }
}

.member-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.member-card {
  position: relative;
  min-width: 0;
  padding: 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;

  strong {
    margin-right: 8px;
    font-weight: bold;
    font-size: 14px;
    color: #000000;
  }

  .member-info {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }

  .member-phone {
    font-weight: 400;
    font-size: 14px;
    color: #667182;
  }

  span {
    margin-right: 8px;
  }

  .el-tag {
    font-weight: 400;
    font-size: 13px;
  }

  .member-body {
    margin: 12px 0 8px;
  }

  .member-body-item {
    display: block;
    margin: 0 0 8px;
    font-weight: 400;
    font-size: 14px;
    color: #667182;

    b {
      font-weight: bold;
      font-size: 14px;
      color: #000000;
      margin-right: 0;
    }

    .coupon-link {
      cursor: pointer;
    }
  }

  .member-foot {
    display: flex;
    justify-content: space-between;
    align-items: center;

    small {
      font-weight: 400;
      font-size: 13px;
      color: #A4AFBA;
    }

    .member-foot-item {
      font-weight: 400;
      font-size: 13px;
      color: #667182;
      cursor: pointer;
    }
  }
}

.coupon-filter {
  margin-bottom: 16px;
}

.coupon-empty {
  padding: 24px 0 8px;
  text-align: center;
  color: #c0c4cc;
}

.coupon-status {
  &.unused {
    color: #1890ff;
  }

  &.used {
    color: #a4afba;
  }

  &.expired {
    color: #f54a45;
  }
}

.visit-filter {
  margin-bottom: 8px;

  /deep/ .el-form-item {
    margin-bottom: 12px;
  }

  /deep/ .el-date-editor {
    width: 340px;
  }

  /deep/ .el-select {
    width: 160px;
  }
}

.table-pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}

.operation-record {
  padding: 2px 10px 14px;
  cursor: pointer;
}

.operation-title {
  font-size: 12px;
  font-weight: 600;
}

.operation-list {
  padding-top: 10px;
}

.operation-row {
  display: flex;
  padding: 5px 0;
  font-size: 12px;

  span:first-child {
    width: 145px;
    color: #c0c4cc;
  }

  span:nth-child(2) {
    width: 90px;
    color: #909399;
  }

  strong {
    font-weight: 400;
  }
}

.operation-record-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #667182;
  margin-bottom: 20px;
}

.empty-text,
.profile-empty {
  padding: 28px;
  text-align: center;
  color: #c0c4cc;
}

.profile-empty {
  flex: 1;
}

.advanced-form {
  .el-date-editor {
    width: 340px;
  }
}

.full-item {
  width: 100%;
}

.move-select {
  width: 260px;
}

.dialog-tip {
  margin: 12px 0;
  font-weight: 600;
}


.auto-tags-warp {
  display: flex;
  align-items: center;

  .auto-tags {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;

    .auto-tag-item {
      height: 26px;
      line-height: 26px;
      padding: 0 8px;
      background-color: #E6F7FF;
      border-radius: 4px;
      border: 1px solid #3195FF;
      font-weight: 400;
      font-size: 14px;
      color: #3195FF;
      box-sizing: border-box;
    }
  }
}

.infoTag {
  color: #FE8F1F;
}

.normalTag {
  color: #A4AFBA;
}

.dialogEdit {
  .el-form-item {
    width: 50%;
    margin-right: 0;
  }
}
.profile-dialog{
  .patient-name-item{
    position: relative;
    .error-tip{
      position: absolute;
      bottom: -28px;
      left: 0;
      color: #f56c6c;
      font-size: 12px;
    }
  }
}
@media (max-width: 1100px) {
  .patient-sidebar {
    width: 190px;
  }

  .overview-grid,
  .summary-grid,
  .member-list,
  .stat-grid {
    grid-template-columns: 1fr;
  }
}
</style>